/**
 * SEO Configuration and Utilities for ShopKaBot
 * Adapted from Subject Buddy's SEO system for B2B AI automation platform
 */

const FALLBACK_SITE_URL = "https://www.shopkabot.com";
const FALLBACK_SITE_NAME = "ShopKaBot";
const FALLBACK_LOGO_URL = `${FALLBACK_SITE_URL}/logo_shopkabot.svg`;

function getEnvironmentList(value?: string) {
  return (value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export const SEO_CONFIG = {
  siteName: process.env.REACT_APP_NAME || FALLBACK_SITE_NAME,
  author: process.env.REACT_APP_AUTHOR || FALLBACK_SITE_NAME,
  twitterHandle: process.env.REACT_APP_TWITTER_HANDLE || "@ShopKaBotAI",
  siteUrl: (process.env.REACT_APP_SITE_URL || FALLBACK_SITE_URL).replace(
    /\/+$/,
    ""
  ),
  defaultTitle: "ShopKaBot - AI Chatbot for WhatsApp Business Automation",
  defaultDescription:
    "Deploy AI-powered agents on WhatsApp to automate sales, customer support, order management, and lead qualification for your business.",
  defaultImage: process.env.REACT_APP_DEFAULT_OG_IMAGE || FALLBACK_LOGO_URL,
  defaultImageAlt: "ShopKaBot - AI Agents for WhatsApp Business Automation",
  locale: "en_US",
  email: process.env.REACT_APP_EMAIL || "support@shopkabot.com",
  sameAs: getEnvironmentList(process.env.REACT_APP_SOCIAL_LINKS),
};

export function buildCanonicalUrl(path = "/") {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${SEO_CONFIG.siteUrl}${normalizedPath}`;
}

export function buildAbsoluteUrl(path?: string) {
  if (!path) {
    return SEO_CONFIG.defaultImage;
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return buildCanonicalUrl(path.startsWith("/") ? path : `/${path}`);
}

export function stripHtml(html: string) {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength - 3).trim()}...`;
}

export function createBlogDescription(blogBody?: string) {
  if (!blogBody) {
    return "Discover AI automation insights, WhatsApp business strategies, and chatbot implementation guides from ShopKaBot.";
  }

  return truncateText(stripHtml(blogBody), 155);
}

export function createOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SEO_CONFIG.siteName,
    url: SEO_CONFIG.siteUrl,
    logo: SEO_CONFIG.defaultImage,
    ...(SEO_CONFIG.email ? { email: SEO_CONFIG.email } : {}),
    ...(SEO_CONFIG.sameAs.length ? { sameAs: SEO_CONFIG.sameAs } : {}),
    areaServed: "Worldwide",
    industryServed: ["Retail", "E-commerce", "Hospitality", "Services"],
  };
}

export function createWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SEO_CONFIG.siteName,
    url: SEO_CONFIG.siteUrl,
    inLanguage: "en-US",
    description: SEO_CONFIG.defaultDescription,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SEO_CONFIG.siteUrl}/blogs?search={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function createBreadcrumbSchema(
  items: Array<{ name: string; path: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: buildCanonicalUrl(item.path),
    })),
  };
}

export function createFaqSchema(
  faqs: Array<{ question: string; answer: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function createServiceSchema(serviceData: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceData.name,
    description: serviceData.description,
    areaServed: "Worldwide",
    serviceType: "Business Automation",
    provider: {
      "@type": "Organization",
      name: SEO_CONFIG.siteName,
      url: SEO_CONFIG.siteUrl,
    },
    url: buildCanonicalUrl(serviceData.url),
  };
}

export function createArticleSchema(articleData: {
  title: string;
  description: string;
  body?: string;
  path: string;
  publishedTime?: string;
  modifiedTime?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: articleData.title,
    description: articleData.description,
    image: buildAbsoluteUrl(articleData.image),
    url: buildCanonicalUrl(articleData.path),
    datePublished: articleData.publishedTime || new Date().toISOString(),
    dateModified: articleData.modifiedTime || new Date().toISOString(),
    author: {
      "@type": "Organization",
      name: SEO_CONFIG.siteName,
    },
    publisher: {
      "@type": "Organization",
      name: SEO_CONFIG.siteName,
      logo: {
        "@type": "ImageObject",
        url: SEO_CONFIG.defaultImage,
      },
    },
  };
}

export function createProductSchema(productData: {
  name: string;
  description: string;
  price?: string;
  url: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: productData.name,
    description: productData.description,
    url: buildCanonicalUrl(productData.url),
    image: buildAbsoluteUrl(productData.image),
    brand: {
      "@type": "Brand",
      name: SEO_CONFIG.siteName,
    },
    ...(productData.price && {
      offers: {
        "@type": "Offer",
        price: productData.price,
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: buildCanonicalUrl(productData.url),
      },
    }),
  };
}
